import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Building2, CheckCircle2, AlertCircle, ArrowLeft } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CorporateSignup = () => {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [domainCheck, setDomainCheck] = useState(null);
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    if (!email.includes('@') || email.split('@')[1]?.length < 3) {
      setDomainCheck(null);
      return;
    }
    setChecking(true);
    const t = setTimeout(async () => {
      try {
        const r = await axios.post(`${BACKEND_URL}/api/corporate/check-domain`, { email });
        setDomainCheck(r.data);
      } catch (_e) {
        setDomainCheck(null);
      } finally {
        setChecking(false);
      }
    }, 500);
    return () => clearTimeout(t);
  }, [email]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/corporate/signup`,
        { name, email, password },
        { withCredentials: true }
      );
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Signup failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F1EA] flex flex-col" data-testid="corporate-signup-page">
      <div className="p-6">
        <Link
          to="/corporate"
          className="inline-flex items-center gap-2 text-[#525252] hover:text-[#0A0A0A] transition-colors text-sm font-medium"
          data-testid="back-link"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Corporate
        </Link>
      </div>

      <div className="flex-1 flex items-center justify-center px-6 py-8">
        <div className="w-full max-w-md">
          <div className="text-center mb-10">
            <div className="w-14 h-14 mx-auto rounded-xl bg-[#0A0A0A] flex items-center justify-center mb-6">
              <Building2 className="w-6 h-6 text-[#B8935A]" />
            </div>
            <h1 className="text-3xl sm:text-4xl font-heading font-bold text-[#0A0A0A] tracking-tight mb-2">Join your team</h1>
            <p className="text-[#525252] text-sm">Sign up with your work email</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className="text-[#0A0A0A] text-sm font-medium mb-2 block">Full Name</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Jane Doe"
                className="bg-white border-[#0A0A0A]/15 focus:border-[#0A0A0A] focus:ring-0 rounded-xl h-12 text-[#0A0A0A]"
                data-testid="corp-signup-name"
                required
              />
            </div>

            <div>
              <Label className="text-[#0A0A0A] text-sm font-medium mb-2 block">Work Email</Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@yourcompany.com"
                className="bg-white border-[#0A0A0A]/15 focus:border-[#0A0A0A] focus:ring-0 rounded-xl h-12 text-[#0A0A0A]"
                data-testid="corp-signup-email"
                required
              />
              {checking && (
                <p className="text-xs text-[#525252] mt-2">Checking your domain…</p>
              )}
              {!checking && domainCheck?.approved && (
                <div className="flex items-center gap-2 mt-2 text-green-700 text-sm" data-testid="domain-approved">
                  <CheckCircle2 className="w-4 h-4" />
                  <span>{domainCheck.company_name} — approved corporate domain</span>
                </div>
              )}
              {!checking && domainCheck && !domainCheck.approved && (
                <div className="flex items-start gap-2 mt-2 text-amber-700 text-sm" data-testid="domain-rejected">
                  <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>
                    {domainCheck.reason === 'public_domain'
                      ? 'Use your work email — public domains like gmail.com are not allowed.'
                      : 'Your company domain is not approved yet. Ask your admin or contact support@soldigital.in.'}
                  </span>
                </div>
              )}
            </div>

            <div>
              <Label className="text-[#0A0A0A] text-sm font-medium mb-2 block">Password</Label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="bg-white border-[#0A0A0A]/15 focus:border-[#0A0A0A] focus:ring-0 rounded-xl h-12 text-[#0A0A0A]"
                data-testid="corp-signup-password"
                required
                minLength={6}
              />
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-800 rounded-xl p-3 text-sm" data-testid="corp-signup-error">
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={loading || !domainCheck?.approved}
              className="w-full bg-[#0A0A0A] hover:bg-[#1a1a1a] text-white rounded-full font-semibold h-12 disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="corp-signup-submit"
            >
              {loading ? 'Creating account…' : 'Create account'}
            </Button>
          </form>

          <p className="text-center text-[#525252] text-sm mt-8">
            Already have an account?{' '}
            <Link to="/login" className="text-[#0A0A0A] font-semibold hover:underline">Log in</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default CorporateSignup;
